import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Loader2,
  Phone,
  Plus,
  RefreshCw,
  ShoppingCart,
  Trash2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { createWhatsappNumberCheckout } from '@/utils/brdidClient'
import {
  getAreaLocalFromLocalidade,
  getLocalidadeLabel,
  shouldIncludeLocalidade,
} from '@/utils/brdidLocalidades'
import { fetchAvailableDids, fetchLocalidades } from '@/utils/brdidListApi'
import { cn } from '@/utils/cn'
import { supabase } from '@/supabaseClient'
import {
  VOICE_EXTRA_NUMBER_BASE_BRL,
  getVoiceExtraUnitPriceBrlForDisplay,
  VOICE_PRICING_HELP,
} from '@/config/voicePricing'

const DOCS_URL = 'https://brdid.com.br/api-docs/#/did/adquirir_novo_did'

function formatDidNumber(value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
  }
  return value || ''
}

function resolveNumberLabel(item) {
  if (typeof item === 'string') return item
  const cn = item.CN != null ? String(item.CN) : ''
  const numero = item.NUMERO || ''
  const main = [cn && `(${cn})`, numero].filter(Boolean).join(' ')
  return main || 'Número'
}

function getDidId(item) {
  if (typeof item === 'string') return item
  return item.CODIGO || `${item.CN || ''}-${item.NUMERO || ''}`
}

function normalizeDidDigits(item) {
  if (typeof item === 'string') return item.replace(/\D/g, '')
  const cn = String(item.CN || '').replace(/\D/g, '')
  const num = String(item.NUMERO || '').replace(/\D/g, '')
  return `${cn}${num}`
}

/**
 * Checkout de números avulsos: lista à esquerda, carrinho à direita (desktop).
 */
export default function BrdidAvulsoCheckout({ profileId }) {
  const [loadingList, setLoadingList] = useState(true)
  const [loadingLocalidades, setLoadingLocalidades] = useState(true)
  const [localidades, setLocalidades] = useState([])
  const [localidadesError, setLocalidadesError] = useState(null)
  const [buying, setBuying] = useState(false)
  const [error, setError] = useState(null)
  const [dids, setDids] = useState([])
  const [areaLocal, setAreaLocal] = useState('31000')
  const [cart, setCart] = useState([])
  const [unitPriceBrl, setUnitPriceBrl] = useState(VOICE_EXTRA_NUMBER_BASE_BRL)
  const [loadingPriceHint, setLoadingPriceHint] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function loadPriceTier() {
      if (!profileId) {
        setUnitPriceBrl(VOICE_EXTRA_NUMBER_BASE_BRL)
        setLoadingPriceHint(false)
        return
      }
      setLoadingPriceHint(true)
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('selected_plan_id, plan_status')
        .eq('id', profileId)
        .maybeSingle()
      if (cancelled) return
      if (error || !profile?.selected_plan_id) {
        setUnitPriceBrl(VOICE_EXTRA_NUMBER_BASE_BRL)
        setLoadingPriceHint(false)
        return
      }
      const { data: planRow } = await supabase
        .from('plans')
        .select('stripe_price_id')
        .eq('id', profile.selected_plan_id)
        .maybeSingle()
      if (cancelled) return
      setUnitPriceBrl(
        getVoiceExtraUnitPriceBrlForDisplay({
          planStripePriceId: planRow?.stripe_price_id,
          planStatus: profile.plan_status,
        })
      )
      setLoadingPriceHint(false)
    }
    loadPriceTier()
    return () => {
      cancelled = true
    }
  }, [profileId])

  const localidadesFiltradas = useMemo(() => {
    const uniqueByArea = new Map()
    for (const item of localidades) {
      if (!shouldIncludeLocalidade(item)) continue
      const area = getAreaLocalFromLocalidade(item)
      if (!area) continue
      if (!uniqueByArea.has(area)) {
        uniqueByArea.set(area, item)
      }
    }
    return Array.from(uniqueByArea.values()).sort((a, b) =>
      getAreaLocalFromLocalidade(a).localeCompare(getAreaLocalFromLocalidade(b))
    )
  }, [localidades])

  const cartKeys = useMemo(() => new Set(cart.map((c) => c.cartKey)), [cart])
  const cartDigits = useMemo(() => new Set(cart.map((c) => c.normalized)), [cart])

  const loadDidsForArea = useCallback(async (code) => {
    const key = String(code || '31000').trim() || '31000'
    setLoadingList(true)
    setError(null)
    try {
      const list = await fetchAvailableDids(key)
      setDids(list)
    } catch (e) {
      console.error('[BrdidAvulsoCheckout]', e)
      setError(e?.message || 'Não foi possível carregar os números disponíveis.')
      setDids([])
    } finally {
      setLoadingList(false)
    }
  }, [])

  const refreshLocalidadesAndDids = useCallback(async () => {
    setLoadingLocalidades(true)
    setLocalidadesError(null)
    try {
      const locs = await fetchLocalidades()
      const arr = Array.isArray(locs) ? locs : []
      setLocalidades(arr)
      const withCode = arr.filter((item) => shouldIncludeLocalidade(item))
      const code =
        withCode.length > 0 ? getAreaLocalFromLocalidade(withCode[0]) || '31000' : '31000'
      setAreaLocal(code)
      await loadDidsForArea(code)
    } catch (e) {
      console.error('[BrdidAvulsoCheckout] localidades', e)
      setLocalidadesError(e?.message || 'Não foi possível carregar localidades.')
      setLocalidades([])
      setAreaLocal('31000')
      await loadDidsForArea('31000')
    } finally {
      setLoadingLocalidades(false)
    }
  }, [loadDidsForArea])

  useEffect(() => {
    let cancelled = false
    async function init() {
      setLoadingLocalidades(true)
      setLocalidadesError(null)
      try {
        const locs = await fetchLocalidades()
        if (cancelled) return
        const arr = Array.isArray(locs) ? locs : []
        setLocalidades(arr)
        const withCode = arr.filter((item) => shouldIncludeLocalidade(item))
        const code =
          withCode.length > 0 ? getAreaLocalFromLocalidade(withCode[0]) || '31000' : '31000'
        setAreaLocal(code)
        await loadDidsForArea(code)
      } catch (e) {
        if (!cancelled) {
          console.error('[BrdidAvulsoCheckout] localidades', e)
          setLocalidadesError(e?.message || 'Não foi possível carregar localidades.')
          setLocalidades([])
          setAreaLocal('31000')
          await loadDidsForArea('31000')
        }
      } finally {
        if (!cancelled) setLoadingLocalidades(false)
      }
    }
    init()
    return () => {
      cancelled = true
    }
  }, [loadDidsForArea])

  function addToCart(item) {
    const cartKey = getDidId(item)
    const normalized = normalizeDidDigits(item)
    if (!normalized) {
      toast.error('Número inválido.')
      return
    }
    if (cartKeys.has(cartKey) || cartDigits.has(normalized)) {
      toast.error('Este número já está no carrinho.')
      return
    }
    setCart((prev) => [
      ...prev,
      {
        cartKey,
        item,
        label: resolveNumberLabel(item),
        normalized,
      },
    ])
    toast.success('Número adicionado ao carrinho.')
  }

  function removeFromCart(cartKey) {
    setCart((prev) => prev.filter((c) => c.cartKey !== cartKey))
  }

  const totalBrl = cart.length * unitPriceBrl

  const handleFinalize = async () => {
    if (!profileId) {
      toast.error('Perfil não carregado.')
      return
    }
    if (cart.length === 0) {
      toast.error('Adicione ao menos um número ao carrinho.')
      return
    }
    try {
      setBuying(true)
      const didNumbers = cart.map((c) => c.normalized)
      await createWhatsappNumberCheckout({ profileId, didNumbers })
      toast.success('Redirecionando para o pagamento…')
    } catch (e) {
      toast.error(e?.message || 'Erro ao iniciar checkout.')
    } finally {
      setBuying(false)
    }
  }

  return (
    <section className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
      <h3 className="text-base font-semibold text-foreground">Contratar novo número</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Escolha DIDs disponíveis, adicione ao carrinho e finalize em um único pagamento no Stripe.
      </p>

      <div className="mt-4 rounded-xl border border-border bg-muted/15 p-4 text-sm">
        <p className="font-semibold text-foreground">Base de preços (número adicional / avulso)</p>
        <ul className="mt-2 space-y-1.5 text-muted-foreground">
          <li>
            <span className="text-foreground">{VOICE_PRICING_HELP.baseLabel}:</span>{' '}
            {VOICE_PRICING_HELP.baseValue.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}
            /mês por número
          </li>
          <li>
            <span className="text-foreground">{VOICE_PRICING_HELP.proLabel}:</span>{' '}
            {VOICE_PRICING_HELP.proValue.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}
            /mês por número
          </li>
        </ul>
        <p className="mt-2 text-xs text-muted-foreground">
          {loadingPriceHint
            ? 'Carregando sua faixa…'
            : `Estimativa para sua conta: ${unitPriceBrl.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} por número. O valor final no checkout segue sua assinatura Voice ativa no Stripe.`}
        </p>
      </div>

      <div className="mt-4 flex flex-wrap items-start gap-3 rounded-xl border border-muted bg-muted/20 p-4 text-sm text-muted-foreground">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
        <p>
          O mesmo <code className="rounded bg-muted px-1">profile_id</code> do WappDesk é usado aqui;
          após o pagamento, cada DID é registrado na BRDID via{' '}
          <code className="rounded bg-muted px-1 text-xs">adquirir_novo_did</code>.
        </p>
      </div>

      {(loadingLocalidades || loadingList) && (
        <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          {loadingLocalidades ? 'Carregando localidades…' : 'Carregando números…'}
        </div>
      )}

      {localidadesError && (
        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          {localidadesError} (listagem de DIDs usa área padrão até corrigir.)
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_minmax(280px,380px)] lg:items-start">
        <div className="min-w-0 space-y-4">
          {!loadingLocalidades && (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <label className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground">
                    Localidade{' '}
                    <span className="font-normal text-muted-foreground">(DIDs disponíveis)</span>
                  </span>
                  <select
                    value={areaLocal}
                    onChange={(e) => {
                      const v = e.target.value
                      setAreaLocal(v)
                      loadDidsForArea(v)
                    }}
                    disabled={localidadesFiltradas.length === 0}
                    className="min-h-[44px] w-full max-w-md rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {localidadesFiltradas.length === 0 ? (
                      <option value={areaLocal}>
                        {localidades.length === 0
                          ? 'Nenhuma localidade retornada pela API'
                          : 'Nenhum código de área nos itens'}
                      </option>
                    ) : (
                      localidadesFiltradas.map((item, idx) => {
                        const code = getAreaLocalFromLocalidade(item)
                        const label = getLocalidadeLabel(item)
                        return (
                          <option key={`${code}-${idx}`} value={String(code)}>
                            {label}
                          </option>
                        )
                      })
                    )}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => refreshLocalidadesAndDids()}
                  disabled={loadingLocalidades || loadingList}
                  className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50 touch-manipulation"
                >
                  <RefreshCw
                    className={cn('h-4 w-4', (loadingLocalidades || loadingList) && 'animate-spin')}
                  />
                  Atualizar
                </button>
              </div>

              {!loadingList && error && (
                <div className="flex gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p>{error}</p>
                    <p className="mt-1 text-xs opacity-90">
                      Token/proxy: <code className="rounded bg-muted px-1">VITE_BRDID_API_TOKEN</code>{' '}
                      ou <code className="rounded bg-muted px-1">VITE_SERVER_URL</code>.
                    </p>
                  </div>
                </div>
              )}

              {!loadingList && !error && (
                <>
                  {dids.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Nenhum DID disponível para esta localidade. Escolha outra ou fale com o suporte.
                    </p>
                  ) : (
                    <ul className="max-h-[min(420px,55vh)] space-y-2 overflow-y-auto overflow-x-hidden pr-1">
                      {dids.map((item) => {
                        const id = getDidId(item)
                        const label = resolveNumberLabel(item)
                        const digits = normalizeDidDigits(item)
                        const inCart =
                          cartKeys.has(id) || (digits && cartDigits.has(digits))
                        return (
                          <li key={id}>
                            <div
                              className={cn(
                                'flex min-h-[52px] flex-col gap-2 rounded-xl border px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between',
                                inCart
                                  ? 'border-primary/40 bg-primary/5'
                                  : 'border-border bg-background'
                              )}
                            >
                              <div className="flex min-w-0 items-center gap-2">
                                <Phone className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                                <span className="truncate text-sm font-medium">{label}</span>
                              </div>
                              <div className="flex shrink-0 items-center gap-2 sm:justify-end">
                                {inCart ? (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-xs font-medium text-primary">
                                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                                    No carrinho
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => addToCart(item)}
                                    className="inline-flex min-h-[40px] min-w-[9rem] items-center justify-center gap-1.5 rounded-lg border border-primary bg-primary/10 px-3 text-xs font-semibold text-primary hover:bg-primary/15 touch-manipulation"
                                  >
                                    <Plus className="h-4 w-4" aria-hidden />
                                    Adicionar
                                  </button>
                                )}
                              </div>
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </>
              )}
            </>
          )}

          <a
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            Documentação BRDID
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        </div>

        <aside className="min-w-0 lg:sticky lg:top-4">
          <div className="rounded-2xl border border-border bg-muted/20 p-4 shadow-sm">
            <div className="flex items-center gap-2 border-b border-border pb-3">
              <ShoppingCart className="h-5 w-5 text-muted-foreground" aria-hidden />
              <div>
                <p className="text-sm font-semibold text-foreground">Carrinho</p>
                <p className="text-xs text-muted-foreground">Resumo do pedido</p>
              </div>
            </div>

            {cart.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nenhum número selecionado. Adicione um ou mais DIDs à esquerda.
              </p>
            ) : (
              <ul className="mt-3 max-h-[min(280px,40vh)] space-y-2 overflow-y-auto">
                {cart.map((row) => (
                  <li
                    key={row.cartKey}
                    className="flex items-start justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-medium text-foreground">
                        {formatDidNumber(row.normalized)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {unitPriceBrl.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}{' '}
                        / mês
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFromCart(row.cartKey)}
                      className="shrink-0 rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive touch-manipulation"
                      aria-label="Remover do carrinho"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 space-y-2 border-t border-border pt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal ({cart.length} número(s))</span>
                <span className="font-semibold text-foreground">
                  {totalBrl.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Cobrança recorrente mensal após confirmação do cartão (conforme Stripe).
              </p>
            </div>

            <button
              type="button"
              onClick={handleFinalize}
              disabled={
                buying || loadingList || loadingLocalidades || cart.length === 0 || !profileId
              }
              className="mt-4 flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:opacity-95 disabled:opacity-50 touch-manipulation"
            >
              {buying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Abrindo checkout…
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4" aria-hidden />
                  Finalizar compra
                </>
              )}
            </button>
          </div>
        </aside>
      </div>
    </section>
  )
}
